import { proxy, snapshot, subscribe, useSnapshot } from 'valtio'
import { deepClone, devtools } from 'valtio/utils'

import type { Cue } from '~shared/rundown/cue'
import type { IndexedCue } from '~shared/utils'
import {
  buildIndexedCues,
  fastDeepEqual,
  firstPlayableCueId,
  flattenCueOrderItems,
  sortCuesByOrder,
} from '~shared/utils'

import { cuesStoreReadonly, getCue } from '../data/cues.store'
import { rundownPageStore } from '../rundown-page.store'

import { cueUiStoreReadonly } from './cue-ui.store'
import {
  buildPlayableNeighborsMap,
  buildSoftSchedulePath,
  filterCollapsedIndexedCues,
  filterSkippedIndexedCues,
  groupCueIds,
} from './cue.helpers'
import type { PlayableNeighbors } from './cue.helpers'

import type { Snapshot } from 'valtio'

// =============== TYPES

type CuesDerived = {
  /** Cue ids in document order. Includes ids whose document has not arrived. */
  flatCueIds: string[]

  /** Loaded cue documents in document order. */
  sortedCues: Cue[]

  /** Every row, with display indices. See `buildIndexedCues`. */
  indexedCues: IndexedCue[]
  indexedCuesMap: Record<string, IndexedCue>

  /** What the editor renders: children of collapsed groups are left out. */
  visibleIndexedCues: IndexedCue[]

  /** What the output views render: cues skipped during the show are gone. */
  indexedCuesWithoutSkipped: IndexedCue[]
  flatCueIdsWithoutSkipped: string[]

  /**
   * First PLAYABLE cue: headings and groups are not candidates.
   *
   * Special because it takes its start time from the rundown, which makes it a
   * fixed start whatever its own `startMode` says.
   */
  firstCueId: string | null

  /** Closest playable neighbors per row. See `buildPlayableNeighborsMap`. */
  playableNeighborsMap: Record<string, PlayableNeighbors>

  /**
   * Rows that lead into a soft-scheduled cue, keyed by cue id so a row reads
   * its own answer in O(1). See `buildSoftSchedulePath`.
   */
  softSchedulePathCueIds: Record<string, true>

  /** Ids of every group, for "collapse all". */
  groupCueIds: string[]

  /** Cues belonging to this rundown. Drives the plan's cue limit. */
  cueCount: number
}

const initialState: CuesDerived = {
  flatCueIds: [],
  sortedCues: [],
  indexedCues: [],
  indexedCuesMap: {},
  visibleIndexedCues: [],
  indexedCuesWithoutSkipped: [],
  flatCueIdsWithoutSkipped: [],
  firstCueId: null,
  playableNeighborsMap: {},
  softSchedulePathCueIds: {},
  groupCueIds: [],
  cueCount: 0,
}

// =============== STORE

/**
 * Derived views of cues (eg. ordered, indexed, filtered).
 *
 * Cue documents live in `cuesStore`, keyed by id and unordered. Their order is
 * a tree of ids on `rundown.cues`. Every list the UI renders is a join of the
 * two, computed once here instead of in each component.
 *
 * @remarks
 *   Read-only outside this module: it is recomputed from the three source
 *   stores, so any write would be overwritten by the next snapshot.
 *
 * @privateRemarks
 *
 *   ### Why is there no reset function?
 *
 *   Emptying the source stores on teardown notifies this one automatically, so
 *   a dedicated reset/clear function is not needed.
 *
 *   ### Why the derivations walk the order and look cues up one by one:
 *
 *   Order is the authority on what belongs to the rundown, so a cue document
 *   with no place in the tree is not a row, and an id whose document has not
 *   arrived is not dropped from `flatCueIds`. The two Firestore snapshots are
 *   independent and arrive in either order, and `recompute` runs on both, so
 *   the join settles whichever way round they land.
 *
 *   ### Why this is NOT using `computed` from `valtio-reactive`:
 *
 *   `computed` tracks dependencies through valtio's `get` trap, which it
 *   installs by patching valtio's handler factory when `valtio-reactive` is
 *   first imported. Proxies created BEFORE that patch keep the unpatched
 *   handler and are invisible to it, forever. Whether this store reacted to
 *   `cuesStore` would then depend on module evaluation order, so reordering
 *   imports in an unrelated file could silently freeze the cue list, with no
 *   type error and no runtime error. Subscribing explicitly is a few more lines
 *   and cannot be broken from a distance.
 */
export const cuesDerived = proxy<CuesDerived>(deepClone(initialState))

/**
 * Rebuild every derived value from the source stores.
 *
 * Runs on any change to the rundown, the cues, or the collapsed groups.
 * Each field is written only when it actually changed (see `assignIfChanged`),
 * which is what keeps a cue title edit from re-rendering components that only
 * read id.
 */
function recompute() {
  const { rundown } = rundownPageStore
  const cueOrder = rundown?.cues ?? []

  const nextFlatCueIds = flattenCueOrderItems(cueOrder)
  const nextSortedCues = sortCuesByOrder(cueOrder, getCue)
  const nextIndexedCues = rundown?.settings
    ? buildIndexedCues(cueOrder, getCue, rundown.settings)
    : []
  const nextWithoutSkipped = filterSkippedIndexedCues(nextIndexedCues, getCue)
  const nextFirstCueId = firstPlayableCueId(cueOrder, toCueMap(nextSortedCues))

  assignIfChanged('flatCueIds', nextFlatCueIds, sameIds)
  assignIfChanged('sortedCues', nextSortedCues, sameCues)
  assignIfChanged('indexedCues', nextIndexedCues, fastDeepEqual)
  assignIfChanged(
    'indexedCuesMap',
    toIndexedCuesMap(nextIndexedCues),
    fastDeepEqual,
  )
  assignIfChanged(
    'visibleIndexedCues',
    filterCollapsedIndexedCues(
      nextIndexedCues,
      cueUiStoreReadonly.collapsedGroups,
    ),
    fastDeepEqual,
  )
  assignIfChanged(
    'indexedCuesWithoutSkipped',
    nextWithoutSkipped,
    fastDeepEqual,
  )
  assignIfChanged(
    'flatCueIdsWithoutSkipped',
    nextWithoutSkipped.map(({ id }) => id),
    sameIds,
  )
  assignIfChanged('firstCueId', nextFirstCueId)
  assignIfChanged(
    'playableNeighborsMap',
    buildPlayableNeighborsMap(nextSortedCues),
    fastDeepEqual,
  )
  assignIfChanged(
    'softSchedulePathCueIds',
    buildSoftSchedulePath(nextFlatCueIds, getCue, nextFirstCueId),
    fastDeepEqual,
  )
  assignIfChanged('groupCueIds', groupCueIds(nextSortedCues), sameIds)
  assignIfChanged('cueCount', nextSortedCues.length)
}

/**
 * Write `next` only if it differs from what is stored.
 *
 * Every recompute builds fresh arrays, so assigning unconditionally would
 * notify subscribers on any field change. The comparison is what makes the
 * fields independent again.
 *
 * @param isEqual How to compare. Defaults to `Object.is`, for scalars.
 */
function assignIfChanged<K extends keyof CuesDerived>(
  key: K,
  next: CuesDerived[K],
  isEqual: (a: CuesDerived[K], b: CuesDerived[K]) => boolean = Object.is,
) {
  if (isEqual(cuesDerived[key], next)) return

  cuesDerived[key] = next
}

/**
 * Compares id lists element by element.
 */
function sameIds(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((id, index) => id === b[index])
}

/**
 * Compares cue lists by identity, not by content.
 *
 * The store merges updates into the existing cue in place, so a cue keeps its
 * proxy identity for as long as it exists. Identity therefore answers exactly
 * the question this list cares about — did the membership or the order change —
 * and an edit to a cue's own fields still reaches subscribers, because valtio
 * propagates a nested change up to the array holding it.
 */
function sameCues(a: readonly Cue[], b: readonly Cue[]): boolean {
  return a.length === b.length && a.every((cue, index) => cue === b[index])
}

function toIndexedCuesMap(indexedCues: IndexedCue[]) {
  const map: Record<string, IndexedCue> = {}
  for (const indexedCue of indexedCues) {
    map[indexedCue.id] = indexedCue
  }
  return map
}

function toCueMap(cues: Cue[]) {
  return new Map(cues.map((cue) => [cue.id, cue]))
}

/**
 * Sources of the derivation.
 *
 * @privateRemarks
 *   Deliberately using batched (default behaviour) instead of passing
 *   `notifyInSync = true`. With it, every document in a bulk snapshot would
 *   trigger a full rebuild, which is quadratic: 2000 cues took ~29s to load,
 *   versus ~44ms batched. The trade-off is that a synchronous/batched read
 *   right after a mutation sees the previous lists until the microtask runs.
 *   That's why `cue.derived.test.ts` flushes before asserting, like the UI.
 */
subscribe(rundownPageStore, recompute)
subscribe(cuesStoreReadonly, recompute)
subscribe(cueUiStoreReadonly, recompute)

// The stores may already hold data by the time this module is first imported.
recompute()

/**
 * Look up one cue's position without subscribing.
 *
 * Use in event handlers, which run after render and need to look up the value
 * once. For components, use `useIndexedCue` to subscribe instead.
 */
export function getIndexedCue(cueId: string): Snapshot<IndexedCue> | undefined {
  return snapshot(cuesDerived).indexedCuesMap[cueId]
}

/**
 * Read the ordered cue list without subscribing.
 *
 * Same contract as {@link getIndexedCue}: use in event handlers, which run
 * after render and need the list once. For components, use `useCuesDerived` to
 * subscribe instead.
 */
export function getSortedCues(): Snapshot<Cue[]> {
  return snapshot(cuesDerived).sortedCues
}

/**
 * Read the first playable cue id without subscribing.
 *
 * Same contract as {@link getIndexedCue}: use in event handlers.
 */
export function getFirstCueId(): string | null {
  return snapshot(cuesDerived).firstCueId
}

/**
 * Rebuild the derived lists now, rather than on the next microtask.
 *
 * The subscriptions above are batched on purpose, which is right for a bulk
 * Firestore snapshot but wrong for a drag and drop: dnd-kit measures the
 * dropped row in a layout effect, so the reordered rows have to be in the DOM
 * in the same commit as the drop. A microtask late and the drag overlay
 * animates back to where the cue started before the list catches up.
 *
 * Call straight after an optimistic order write. Cheap, since `recompute`
 * only assigns fields that actually changed. Pairs with `useCuesDerived`,
 * which is notified synchronously so the rebuild reaches the same commit.
 */
export function flushCuesDerived() {
  recompute()
}

// =============== HOOKS

/**
 * Subscribe to the derived cue state.
 *
 * @remarks
 *   Notified synchronously, so everything derived from a rebuild lands in one
 *   React commit. A drop has to reorder the rows AND renumber them together,
 *   and a row is memoised on its cue id, so re-rendering the list does not
 *   re-render the rows: each one reads its own metadata through this hook. With
 *   valtio's default render batching the list reordered while every row's
 *   index, parent and tree lines stayed a microtask behind.
 *   This is render batching, not the source subscriptions, which stay batched
 *   for the reason given above. `recompute` runs once per batch either way, so
 *   this notifies once per rebuild rather than once per changed document.
 */
export function useCuesDerived() {
  return useSnapshot(cuesDerived, { sync: true })
}

// #region ============== DEV
if (process.env.NODE_ENV !== 'production') {
  devtools(cuesDerived, { name: 'cuesDerived', enabled: true })
}
// #endregion
